import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Calculator, Pause, Play, Plus, Send } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  KeyValue,
  Section,
  Select,
  Spinner,
  Textarea,
  type Tone,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { ContentReviewPanel, reviewTone } from '../components/ContentReview';
import { api, ApiError, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { minorToInput, toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  MessageAudienceEstimate,
  MessageCampaign,
  MessageCampaignReport,
  MessagingSettings,
  SegmentRow,
  SmsPreview,
  SmsTemplate,
  WhatsappTemplateOptions,
} from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';

/**
 * Pazarlama → SMS ve WhatsApp (F6-09, ADR-090): kurulum durumu, ayarlar, kampanyalar (SMS şablonu veya Meta'da
 * onaylı WhatsApp şablonu, segment kitlesi, tahmini maliyet, MFA + Idempotency-Key ile gönder/zamanla, ilerleme,
 * durdur/sürdür, rapor) ve SMS şablonları (canlı boy hesabı, MERSİS/ret alt bilgisi, uzman onayı).
 * Sağlayıcı bağlı değilse ekran "Kurulum Gerekli" gösterir ve gönderim sunucuda reddedilir.
 */

type Status = MessageCampaign['status'];
const statusTone: Record<Status, Tone> = {
  draft: 'neutral',
  scheduled: 'slate',
  sending: 'pine',
  sent: 'moss',
  paused: 'rose',
  cancelled: 'slate',
};
const LIVE: Status[] = ['scheduled', 'sending'];
const SMS_PROVIDERS = ['netgsm', 'iletimerkezi', 'verimor'] as const;
const CAP_OPTIONS = [0, 24, 48, 72, 168, 336, 720];
const MERGE = ['firstName', 'name', 'storeName'] as const;
const SKIP_KEYS = [
  'no_phone',
  'not_mobile',
  'suppressed',
  'no_consent',
  'iys_not_registered',
  'iys_refused',
  'duplicate_phone',
  'frequency_cap',
  'staging_allowlist',
  'invalid_address',
] as const;
const KNOWN_ERRORS = ['whatsapp_opted_out', 'interrupted', 'undelivered', 'whatsapp_failed', 'error'];
const PAUSE_KEYS = [
  'sms_template_required',
  'template_review_required',
  'sms_footer_missing',
  'sms_not_configured',
  'whatsapp_not_configured',
  'provider_invalid_settings',
  'provider_auth_failed',
  'provider_sender_invalid',
  'provider_iys_not_configured',
  'provider_insufficient_balance',
  'provider_template_invalid',
];
const TRY = 'TRY';
const ROUTE = '/sms-whatsapp-kampanyalari';

function toLocalInput(v: string | Date | null): string {
  if (!v) return '';
  const d = new Date(v);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function useDebounced(value: string, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setV(value), ms);
    return () => clearTimeout(h);
  }, [value, ms]);
  return v;
}

function useMessagingSettings() {
  return useQuery({
    queryKey: ['messaging-settings'],
    queryFn: () => api.get<MessagingSettings>('/v1/admin/messaging/settings'),
  });
}

// ---------------------------------------------------------------------------- Kampanya listesi

export function MessageCampaignsPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const settings = useMessagingSettings();
  const list = useQuery({
    queryKey: ['message-campaigns'],
    queryFn: () => api.get<MessageCampaign[]>('/v1/admin/message-campaigns'),
    refetchInterval: (q) => (q.state.data?.some((c) => LIVE.includes(c.status)) ? 5000 : false),
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  const rows = (list.data ?? []).filter((c) => c.storeId === storeId);
  return (
    <PageFrame
      helpKey="page.message_campaigns"
      actions={
        can('marketing.campaign.write') &&
        storeId && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              onClick={() => void navigate({ to: `${ROUTE}/$id`, params: { id: 'yeni' } })}
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.messaging.create')}
            </Button>
            <HelpTip k="messaging.create" />
          </div>
        )
      }
    >
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <div className="space-y-6">
          <p className="max-w-[70ch] text-sm text-ink-2">{t('admin.messaging.lead')}</p>
          {settings.data && <SetupStatus settings={settings.data} />}
          <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
          {!list.data ? (
            <Spinner label={t('common.loading')} />
          ) : !rows.length ? (
            <EmptyState
              title={t('admin.messaging.emptyTitle')}
              description={t('admin.messaging.emptyLead')}
            />
          ) : (
            <div className="overflow-x-auto rounded-md border border-line">
              <table className="w-full text-sm" aria-label={t('admin.messaging.listLabel')}>
                <thead className="bg-surface-2 text-ink-2">
                  <tr>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.messaging.colName')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.messaging.colChannel')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.messaging.colStatus')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.messaging.colSent')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.messaging.colDelivered')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.messaging.colWhen')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.id} className="border-t border-line align-top">
                      <td className="px-3 py-2">
                        <Link
                          to={`${ROUTE}/$id`}
                          params={{ id: c.id }}
                          className="font-medium text-pine hover:underline"
                        >
                          {c.name}
                        </Link>
                        <p className="text-ink-3">{c.smsTemplate?.name ?? c.whatsapp?.name ?? '—'}</p>
                      </td>
                      <td className="px-3 py-2">{t(`admin.messaging.channels.${c.channel}`)}</td>
                      <td className="px-3 py-2">
                        <Badge tone={statusTone[c.status]}>{t(`admin.messaging.statuses.${c.status}`)}</Badge>
                      </td>
                      <td className="px-3 py-2 text-end tabular-nums">
                        {c.progress ? `${c.progress.sent} / ${c.progress.total}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-end tabular-nums">
                        {c.progress ? String(c.progress.delivered) : '—'}
                      </td>
                      <td className="px-3 py-2 text-end text-ink-2">
                        {formatDate(c.finishedAt ?? c.startedAt ?? c.scheduledAt ?? c.updatedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {settings.data && <SettingsSection settings={settings.data} />}
        </div>
      )}
    </PageFrame>
  );
}

/** Kurulum durumu: SMS sağlayıcısı, WhatsApp, MERSİS ve ret metni. Eksikler "Kurulum Gerekli" görünür. */
function SetupStatus({ settings }: { settings: MessagingSettings }) {
  const t = useT();
  const smsLive = !!settings.smsProvider && settings.connected.includes(settings.smsProvider);
  const waLive = settings.connected.includes('whatsapp');
  const setup = <Badge tone="amber">{t('integrationStatus.NOT_CONFIGURED')}</Badge>;
  const link = 'text-pine hover:underline';
  return (
    <Section
      title={t('admin.messaging.setupTitle')}
      description={t('admin.messaging.setupLead')}
      action={<HelpTip k="messaging.setup" />}
      className="border-t-0 pt-0"
    >
      <KeyValue
        items={[
          {
            label: t('admin.messaging.channels.sms'),
            value: smsLive ? (
              <Badge tone="moss">
                {t('admin.messaging.connectedVia', {
                  provider: t(`integrations.${settings.smsProvider}.name`),
                })}
              </Badge>
            ) : (
              <span className="flex flex-wrap items-center gap-2">
                {setup}
                <Link to="/entegrasyonlar" className={link}>
                  {t('admin.messaging.smsSetupLink')}
                </Link>
              </span>
            ),
          },
          {
            label: t('admin.messaging.channels.whatsapp'),
            value: waLive ? (
              <span className="space-y-1">
                <Badge tone="moss">{t('admin.messaging.connected')}</Badge>
                <span className="block text-sm text-ink-2">
                  {t('admin.messaging.webhookUrl')}{' '}
                  <code className="break-all">{settings.whatsappWebhookUrl}</code>
                </span>
              </span>
            ) : (
              <span className="flex flex-wrap items-center gap-2">
                {setup}
                <Link to="/entegrasyonlar" className={link}>
                  {t('admin.messaging.whatsappSetupLink')}
                </Link>
              </span>
            ),
          },
          {
            label: t('admin.messaging.mersis'),
            value: settings.mersisNo ?? (
              <span className="flex flex-wrap items-center gap-2">
                <Badge tone="amber">{t('admin.messaging.missing')}</Badge>
                <Link to="/isletme" className={link}>
                  {t('admin.messaging.mersisLink')}
                </Link>
              </span>
            ),
          },
          {
            label: t('admin.messaging.optOut'),
            value: settings.optOutText ?? <Badge tone="amber">{t('admin.messaging.missing')}</Badge>,
          },
        ]}
      />
    </Section>
  );
}

interface SettingsDraft {
  smsProvider: string;
  smsUnitCost: string;
  whatsappUnitCost: string;
  optOutText: string;
  frequencyCapHours: number;
  stagingPhones: string;
}
const settingsDraftOf = (s: MessagingSettings): SettingsDraft => ({
  smsProvider: s.smsProvider ?? '',
  smsUnitCost: minorToInput(s.smsUnitCostMinor),
  whatsappUnitCost: minorToInput(s.whatsappUnitCostMinor),
  optOutText: s.optOutText ?? '',
  frequencyCapHours: s.frequencyCapHours,
  stagingPhones: s.stagingPhoneAllowlist.join('\n'),
});

function SettingsSection({ settings }: { settings: MessagingSettings }) {
  const t = useT();
  const { can } = usePermissions();
  const canManage = can('automation.manage');
  const [draft, setDraft] = useState<SettingsDraft>(() => settingsDraftOf(settings));
  useEffect(() => setDraft(settingsDraftOf(settings)), [settings]);
  const set = (p: Partial<SettingsDraft>) => setDraft((d) => ({ ...d, ...p }));
  const dirty = JSON.stringify(draft) !== JSON.stringify(settingsDraftOf(settings));
  const smsCost = draft.smsUnitCost.trim() ? toMinor(draft.smsUnitCost, TRY) : null;
  const waCost = draft.whatsappUnitCost.trim() ? toMinor(draft.whatsappUnitCost, TRY) : null;
  const costInvalid =
    (!!draft.smsUnitCost.trim() && smsCost === null) || (!!draft.whatsappUnitCost.trim() && waCost === null);
  const save = useApiMutation(
    () =>
      api.put<MessagingSettings>('/v1/admin/messaging/settings', {
        smsProvider: draft.smsProvider || null,
        smsUnitCostMinor: smsCost,
        whatsappUnitCostMinor: waCost,
        optOutText: draft.optOutText.trim() || null,
        frequencyCapHours: draft.frequencyCapHours,
        stagingPhoneAllowlist: draft.stagingPhones
          .split(/[\n,]/)
          .map((x) => x.trim())
          .filter(Boolean),
      }),
    { invalidate: [['messaging-settings']], success: t('admin.messaging.settingsSaved') },
  );
  const fe = (f: string) => fieldError(save.error, f);
  return (
    <Section
      title={t('admin.messaging.settingsTitle')}
      description={t('admin.messaging.settingsLead')}
      action={<HelpTip k="messaging.settings" />}
    >
      <fieldset disabled={!canManage} className="grid max-w-2xl gap-4">
        <legend className="sr-only">{t('admin.messaging.settingsTitle')}</legend>
        <Field
          label={t('admin.messaging.smsProvider')}
          description={t('admin.messaging.smsProviderLead')}
          help={<HelpTip k="messaging.sms_provider" />}
          error={fe('smsProvider')}
          className="max-w-sm"
        >
          {(p) => (
            <Select {...p} value={draft.smsProvider} onChange={(e) => set({ smsProvider: e.target.value })}>
              <option value="">{t('admin.messaging.noProvider')}</option>
              {SMS_PROVIDERS.map((k) => (
                <option key={k} value={k} disabled={!settings.connected.includes(k)}>
                  {settings.connected.includes(k)
                    ? t(`integrations.${k}.name`)
                    : t('admin.messaging.providerNotConnected', { provider: t(`integrations.${k}.name`) })}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label={t('admin.messaging.optOut')}
          description={t('admin.messaging.optOutLead')}
          help={<HelpTip k="messaging.opt_out" />}
          error={fe('optOutText')}
        >
          {(p) => (
            <Input
              {...p}
              value={draft.optOutText}
              maxLength={80}
              onChange={(e) => set({ optOutText: e.target.value })}
            />
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t('admin.messaging.smsUnitCost')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="messaging.unit_cost_sms" />}
            error={
              draft.smsUnitCost.trim() && smsCost === null
                ? t('admin.messaging.costInvalid')
                : fe('smsUnitCostMinor')
            }
          >
            {(p) => (
              <Input
                {...p}
                inputMode="decimal"
                value={draft.smsUnitCost}
                onChange={(e) => set({ smsUnitCost: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.messaging.whatsappUnitCost')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="messaging.unit_cost_whatsapp" />}
            error={
              draft.whatsappUnitCost.trim() && waCost === null
                ? t('admin.messaging.costInvalid')
                : fe('whatsappUnitCostMinor')
            }
          >
            {(p) => (
              <Input
                {...p}
                inputMode="decimal"
                value={draft.whatsappUnitCost}
                onChange={(e) => set({ whatsappUnitCost: e.target.value })}
              />
            )}
          </Field>
        </div>
        <Field
          label={t('admin.messaging.frequencyCap')}
          description={t('admin.messaging.frequencyCapLead')}
          help={<HelpTip k="messaging.frequency_cap" />}
          className="max-w-sm"
        >
          {(p) => (
            <Select
              {...p}
              value={draft.frequencyCapHours}
              onChange={(e) => set({ frequencyCapHours: Number(e.target.value) })}
            >
              {CAP_OPTIONS.map((h) => (
                <option key={h} value={h}>
                  {h === 0 ? t('admin.messaging.capNone') : t('admin.messaging.capHours', { count: h })}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label={t('admin.messaging.stagingPhones')}
          optionalLabel={t('common.optional')}
          description={t('admin.messaging.stagingPhonesLead')}
          help={<HelpTip k="messaging.staging_phones" />}
          error={fe('stagingPhoneAllowlist')}
        >
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={draft.stagingPhones}
              onChange={(e) => set({ stagingPhones: e.target.value })}
            />
          )}
        </Field>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button onClick={() => save.mutate(undefined)} disabled={!dirty || costInvalid || save.isPending}>
              {t('admin.messaging.saveSettings')}
            </Button>
            <HelpTip k="messaging.settings_save" />
          </div>
        )}
      </fieldset>
    </Section>
  );
}

// ---------------------------------------------------------------------------- Kampanya düzenleyici

interface Draft {
  channel: 'sms' | 'whatsapp';
  name: string;
  smsTemplateId: string;
  waName: string;
  waLanguage: string;
  waParams: string[];
  segmentIds: string[];
}

const emptyDraft = (): Draft => ({
  channel: 'sms',
  name: '',
  smsTemplateId: '',
  waName: '',
  waLanguage: 'tr',
  waParams: [],
  segmentIds: [],
});

const draftOf = (c: MessageCampaign): Draft => ({
  channel: c.channel,
  name: c.name,
  smsTemplateId: c.smsTemplateId ?? '',
  waName: c.whatsapp?.name ?? '',
  waLanguage: c.whatsapp?.language ?? 'tr',
  waParams: c.whatsapp?.params ?? [],
  segmentIds: c.segmentIds,
});

export function MessageCampaignEditorPage() {
  const { id } = useParams({ from: '/app/sms-whatsapp-kampanyalari/$id' });
  const isNew = id === 'yeni';
  const t = useT();
  const q = useQuery({
    queryKey: ['message-campaign', id],
    queryFn: () => api.get<MessageCampaign>(`/v1/admin/message-campaigns/${id}`),
    enabled: !isNew,
    refetchInterval: (s) => (s.state.data && LIVE.includes(s.state.data.status) ? 5000 : false),
  });
  if (!isNew && !q.data) return <Spinner label={t('common.loading')} />;
  return <CampaignEditor key={q.data?.id ?? 'yeni'} campaign={q.data ?? null} />;
}

function CampaignEditor({ campaign }: { campaign: MessageCampaign | null }) {
  const t = useT();
  const { formatDate } = useI18n();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can('marketing.campaign.write');
  const canSend = can('marketing.campaign.send');
  const { stores, storeId: chosenStore, setStoreId } = useStoreChoice();
  const storeId = campaign?.storeId ?? chosenStore;
  const editable = canWrite && (!campaign || campaign.status === 'draft');
  const settings = useMessagingSettings();
  const templates = useQuery({
    queryKey: ['sms-templates'],
    queryFn: () => api.get<SmsTemplate[]>('/v1/admin/sms-templates'),
  });
  const segments = useQuery({
    queryKey: ['segments'],
    queryFn: () => api.get<SegmentRow[]>('/v1/admin/segments'),
    enabled: can('marketing.audience.read'),
  });
  const [draft, setDraft] = useState<Draft>(() => (campaign ? draftOf(campaign) : emptyDraft()));
  useEffect(() => {
    if (campaign) setDraft(draftOf(campaign));
  }, [campaign]);
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const dirty = !campaign || JSON.stringify(draft) !== JSON.stringify(draftOf(campaign));
  const wa = useQuery({
    queryKey: ['whatsapp-templates'],
    queryFn: () => api.get<WhatsappTemplateOptions>('/v1/admin/whatsapp/templates'),
    enabled: draft.channel === 'whatsapp',
    retry: false,
  });
  const chosenSms = (templates.data ?? []).find((x) => x.id === draft.smsTemplateId);
  const waTemplate = wa.data?.templates.find(
    (x) => x.name === draft.waName && x.language === draft.waLanguage,
  );
  const complete =
    draft.name.trim().length >= 2 &&
    draft.segmentIds.length > 0 &&
    (draft.channel === 'sms'
      ? !!draft.smsTemplateId
      : !!draft.waName.trim() && !!draft.waLanguage.trim() && draft.waParams.every((p) => !!p.trim()));

  const body = () => ({
    name: draft.name,
    segmentIds: draft.segmentIds,
    ...(draft.channel === 'sms'
      ? { smsTemplateId: draft.smsTemplateId || null }
      : {
          whatsapp: { name: draft.waName.trim(), language: draft.waLanguage.trim(), params: draft.waParams },
        }),
  });
  const save = useApiMutation(
    () =>
      campaign
        ? api.patch<MessageCampaign>(`/v1/admin/message-campaigns/${campaign.id}`, {
            ...body(),
            version: campaign.version,
          })
        : api.post<MessageCampaign>('/v1/admin/message-campaigns', {
            ...body(),
            channel: draft.channel,
            storeId,
          }),
    {
      invalidate: [['message-campaigns'], ...(campaign ? [['message-campaign', campaign.id]] : [])],
      success: t('admin.messaging.saved'),
      onSuccess: (r) => {
        if (!campaign) void navigate({ to: `${ROUTE}/$id`, params: { id: r.id } });
      },
    },
  );
  const [estimate, setEstimate] = useState<MessageAudienceEstimate | null>(null);
  const estimateM = useApiMutation(
    () =>
      api.post<MessageAudienceEstimate>('/v1/admin/message-campaigns/audience', {
        channel: draft.channel,
        segmentIds: draft.segmentIds,
        smsTemplateId: draft.channel === 'sms' ? draft.smsTemplateId || null : null,
      }),
    { onSuccess: (r) => setEstimate(r) },
  );
  useEffect(() => setEstimate(null), [draft.segmentIds, draft.channel, draft.smsTemplateId]);
  const remove = useApiMutation(() => api.del(`/v1/admin/message-campaigns/${campaign!.id}`), {
    invalidate: [['message-campaigns']],
    success: t('admin.messaging.deleted'),
    onSuccess: () => void navigate({ to: ROUTE }),
  });
  const cancel = useApiMutation(
    () =>
      api.post<MessageCampaign>(`/v1/admin/message-campaigns/${campaign!.id}/cancel`, {
        version: campaign!.version,
      }),
    {
      invalidate: [['message-campaigns'], ['message-campaign', campaign?.id]],
      success: t('admin.messaging.cancelled'),
    },
  );
  const resumeIdem = useRef(new IdempotencyKey());
  const resume = useApiMutation(
    () => {
      const b = { version: campaign!.version };
      return api.post<MessageCampaign>(`/v1/admin/message-campaigns/${campaign!.id}/resume`, b, {
        idempotencyKey: resumeIdem.current.for(JSON.stringify(b)),
      });
    },
    {
      invalidate: [['message-campaigns'], ['message-campaign', campaign?.id]],
      success: t('admin.messaging.resumed'),
    },
  );
  const fe = (f: string) => fieldError(save.error, f);
  const smsLive = !!settings.data?.smsProvider && settings.data.connected.includes(settings.data.smsProvider);
  const waLive = !!settings.data?.connected.includes('whatsapp');
  const channelLive = draft.channel === 'sms' ? smsLive : waLive;

  return (
    <PageFrame
      helpKey="page.message_campaign_editor"
      title={campaign ? campaign.name : t('admin.messaging.create')}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {editable && (
            <>
              <Button
                variant={campaign ? 'secondary' : 'primary'}
                onClick={() => save.mutate(undefined)}
                disabled={!dirty || !complete || !storeId || save.isPending}
              >
                {campaign ? t('admin.messaging.save') : t('admin.messaging.createConfirm')}
              </Button>
              <HelpTip k="messaging.save" />
            </>
          )}
          {campaign?.status === 'draft' && canSend && (
            <SendButton campaign={campaign} dirty={dirty} channelLive={channelLive} />
          )}
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Link to={ROUTE} className="text-pine hover:underline">
          {t('admin.messaging.back')}
        </Link>
        {campaign && (
          <Badge tone={statusTone[campaign.status]}>{t(`admin.messaging.statuses.${campaign.status}`)}</Badge>
        )}
        {campaign && editable && dirty && <Badge tone="amber">{t('admin.messaging.unsaved')}</Badge>}
        {settings.data && !channelLive && (
          <Badge tone="amber">
            {t('admin.messaging.channelSetup', { channel: t(`admin.messaging.channels.${draft.channel}`) })}
          </Badge>
        )}
      </div>
      {!campaign && <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />}

      {campaign && campaign.status !== 'draft' && (
        <StatusSection
          campaign={campaign}
          canSend={canSend}
          onCancel={() => cancel.mutateAsync(undefined).catch(() => undefined)}
          onResume={() => resume.mutate(undefined)}
          busy={cancel.isPending || resume.isPending}
        />
      )}

      <fieldset disabled={!editable} className="space-y-8">
        <legend className="sr-only">{t('admin.messaging.editor')}</legend>
        <Section title={t('admin.messaging.content')} className="border-t-0 pt-0">
          <div className="grid max-w-2xl gap-4">
            {!campaign && (
              <fieldset className="space-y-1.5 text-sm">
                <legend className="mb-1 flex items-center gap-1 font-medium">
                  {t('admin.messaging.channel')}
                  <HelpTip k="messaging.channel" />
                </legend>
                {(['sms', 'whatsapp'] as const).map((ch) => (
                  <label key={ch} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="channel"
                      checked={draft.channel === ch}
                      onChange={() => set({ channel: ch })}
                    />
                    {t(`admin.messaging.channels.${ch}`)}
                  </label>
                ))}
              </fieldset>
            )}
            <Field
              label={t('admin.messaging.name')}
              description={t('admin.messaging.nameLead')}
              help={<HelpTip k="messaging.name" />}
              error={fe('name')}
              required
            >
              {(p) => (
                <Input
                  {...p}
                  value={draft.name}
                  maxLength={120}
                  onChange={(e) => set({ name: e.target.value })}
                />
              )}
            </Field>
            {draft.channel === 'sms' ? (
              <>
                <Field
                  label={t('admin.messaging.smsTemplate')}
                  help={<HelpTip k="messaging.sms_template" />}
                  error={fe('smsTemplateId')}
                  required
                >
                  {(p) => (
                    <Select
                      {...p}
                      value={draft.smsTemplateId}
                      onChange={(e) => set({ smsTemplateId: e.target.value })}
                    >
                      <option value="">{t('admin.messaging.chooseTemplate')}</option>
                      {(templates.data ?? []).map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                {chosenSms && (
                  <div className="space-y-1 text-sm">
                    <p className="whitespace-pre-wrap rounded-md border border-line bg-surface px-3 py-2 text-ink-2">
                      {chosenSms.body}
                    </p>
                    <p className="flex flex-wrap items-center gap-2">
                      {(chosenSms.review.required || chosenSms.review.status !== 'not_required') && (
                        <Badge tone={reviewTone[chosenSms.review.status]}>
                          {t(`admin.pages.reviews.${chosenSms.review.status}`)}
                        </Badge>
                      )}
                      <Link
                        to="/sms-sablonlari/$id"
                        params={{ id: chosenSms.id }}
                        className="text-pine hover:underline"
                      >
                        {t('admin.messaging.openTemplate')}
                      </Link>
                    </p>
                  </div>
                )}
                {templates.data && !templates.data.length && (
                  <p className="text-sm text-ink-2">
                    {t('admin.messaging.noTemplates')}{' '}
                    <Link to="/sms-sablonlari" className="text-pine hover:underline">
                      {t('admin.nav.smsTemplates')}
                    </Link>
                  </p>
                )}
              </>
            ) : (
              <WhatsappFields
                draft={draft}
                set={set}
                options={wa.data ?? null}
                error={wa.error instanceof ApiError ? wa.error.title : null}
                fe={fe}
              />
            )}
            {draft.channel === 'whatsapp' && waTemplate && (
              <p className="whitespace-pre-wrap rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink-2">
                {waTemplate.body}
              </p>
            )}
          </div>
        </Section>

        <Section
          title={t('admin.messaging.audience')}
          description={t('admin.messaging.audienceLead')}
          action={<HelpTip k="messaging.segments" />}
        >
          <div className="grid max-w-2xl gap-3">
            {fe('segmentIds') && (
              <p role="alert" className="text-sm font-medium text-rose">
                {fe('segmentIds')}
              </p>
            )}
            <fieldset className="space-y-1.5">
              <legend className="mb-1 text-sm font-medium">{t('admin.campaigns.segments')}</legend>
              {!(segments.data ?? []).length ? (
                <p className="text-sm text-ink-3">{t('admin.campaigns.noSegments')}</p>
              ) : (
                (segments.data ?? []).map((sg) => (
                  <label key={sg.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={draft.segmentIds.includes(sg.id)}
                      onChange={(e) =>
                        set({
                          segmentIds: e.target.checked
                            ? [...draft.segmentIds, sg.id]
                            : draft.segmentIds.filter((x) => x !== sg.id),
                        })
                      }
                    />
                    {sg.name}
                    <span className="text-ink-3">
                      {t('admin.campaigns.segmentCount', { count: sg.memberCount })}
                    </span>
                  </label>
                ))
              )}
            </fieldset>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                onClick={() => estimateM.mutate(undefined)}
                disabled={!draft.segmentIds.length || estimateM.isPending}
              >
                <Calculator className="h-4 w-4" aria-hidden />
                {t('admin.messaging.estimate')}
              </Button>
              <HelpTip k="messaging.estimate" />
            </div>
            {estimate && <EstimateView estimate={estimate} channel={draft.channel} />}
          </div>
        </Section>
      </fieldset>

      {campaign?.startedAt && <ReportSection campaign={campaign} />}

      {campaign &&
        canWrite &&
        (campaign.status === 'draft' || (campaign.status === 'cancelled' && !campaign.startedAt)) && (
          <Section title={t('admin.messaging.dangerZone')}>
            <div className="flex items-center gap-2">
              <ConfirmButton
                message={t('admin.messaging.deleteConfirm', { name: campaign.name })}
                onConfirm={() => remove.mutateAsync(undefined).catch(() => undefined)}
              >
                {t('common.delete')}
              </ConfirmButton>
              <HelpTip k="messaging.delete" />
            </div>
          </Section>
        )}
      {campaign && (
        <p className="text-sm text-ink-3">
          {t('admin.messaging.updated', { date: formatDate(campaign.updatedAt) })}
        </p>
      )}
    </PageFrame>
  );
}

function WhatsappFields({
  draft,
  set,
  options,
  error,
  fe,
}: {
  draft: Draft;
  set: (p: Partial<Draft>) => void;
  options: WhatsappTemplateOptions | null;
  error: string | null;
  fe: (f: string) => string | undefined;
}) {
  const t = useT();
  const list = options?.connected ? options.templates : [];
  const chosen = list.find((x) => x.name === draft.waName && x.language === draft.waLanguage);
  const paramCount = chosen ? chosen.params : draft.waParams.length;
  const params = Array.from({ length: paramCount }, (_, i) => draft.waParams[i] ?? '');
  return (
    <>
      {error && (
        <p role="alert" className="text-sm font-medium text-rose">
          {error}
        </p>
      )}
      {options && !options.connected && (
        <p className="rounded-md border border-amber/30 bg-amber-tint px-3 py-2 text-sm">
          {t('admin.messaging.whatsappNotConnected')}
        </p>
      )}
      {list.length > 0 ? (
        <Field
          label={t('admin.messaging.whatsappTemplate')}
          help={<HelpTip k="messaging.whatsapp_template" />}
          error={fe('whatsapp')}
          required
        >
          {(p) => (
            <Select
              {...p}
              value={chosen ? `${chosen.name}|${chosen.language}` : ''}
              onChange={(e) => {
                const x = list.find((y) => `${y.name}|${y.language}` === e.target.value);
                set({
                  waName: x?.name ?? '',
                  waLanguage: x?.language ?? 'tr',
                  waParams: x ? Array.from({ length: x.params }, (_, i) => draft.waParams[i] ?? '') : [],
                });
              }}
            >
              <option value="">{t('admin.messaging.chooseTemplate')}</option>
              {list.map((x) => (
                <option
                  key={`${x.name}|${x.language}`}
                  value={`${x.name}|${x.language}`}
                  disabled={x.status !== 'APPROVED'}
                >
                  {t('admin.messaging.whatsappOption', {
                    name: x.name,
                    language: x.language,
                    status: t(`admin.messaging.waStatuses.${x.status in WA_STATUS ? x.status : 'OTHER'}`),
                  })}
                </option>
              ))}
            </Select>
          )}
        </Field>
      ) : (
        <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
          <Field
            label={t('admin.messaging.whatsappName')}
            description={t('admin.messaging.whatsappNameLead')}
            help={<HelpTip k="messaging.whatsapp_template" />}
            error={fe('whatsapp.name') ?? fe('whatsapp')}
            required
          >
            {(p) => (
              <Input
                {...p}
                value={draft.waName}
                maxLength={512}
                onChange={(e) => set({ waName: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('admin.messaging.whatsappLanguage')} error={fe('whatsapp.language')} required>
            {(p) => (
              <Input
                {...p}
                value={draft.waLanguage}
                maxLength={15}
                onChange={(e) => set({ waLanguage: e.target.value })}
              />
            )}
          </Field>
        </div>
      )}
      <fieldset className="space-y-2">
        <legend className="mb-1 flex items-center gap-1 text-sm font-medium">
          {t('admin.messaging.whatsappParams')}
          <HelpTip k="messaging.whatsapp_params" />
        </legend>
        {fe('whatsapp.params') && (
          <p role="alert" className="text-sm font-medium text-rose">
            {fe('whatsapp.params')}
          </p>
        )}
        {!params.length && <p className="text-sm text-ink-3">{t('admin.messaging.noParams')}</p>}
        {params.map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              aria-label={t('admin.messaging.paramLabel', { n: i + 1 })}
              value={v}
              maxLength={200}
              onChange={(e) => {
                const next = [...params];
                next[i] = e.target.value;
                set({ waParams: next });
              }}
            />
            {!chosen && (
              <Button size="sm" onClick={() => set({ waParams: params.filter((_, j) => j !== i) })}>
                {t('common.remove')}
              </Button>
            )}
          </div>
        ))}
        {!chosen && params.length < 10 && (
          <Button size="sm" onClick={() => set({ waParams: [...params, ''] })}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('admin.messaging.addParam')}
          </Button>
        )}
        <p className="text-sm text-ink-3">{t('admin.messaging.paramsLead')}</p>
      </fieldset>
    </>
  );
}

const WA_STATUS = { APPROVED: 1, PENDING: 1, REJECTED: 1, PAUSED: 1, DISABLED: 1 } as const;

function EstimateView({
  estimate,
  channel,
}: {
  estimate: MessageAudienceEstimate;
  channel: 'sms' | 'whatsapp';
}) {
  const t = useT();
  const money = useMoney();
  const excluded = Object.entries(estimate.excluded).filter(([, n]) => n > 0);
  return (
    <div className="rounded-md border border-line bg-surface p-3 text-sm" aria-live="polite">
      <p className="font-medium">
        {t('admin.messaging.estimateResult', { eligible: estimate.eligible, total: estimate.total })}
      </p>
      {excluded.length > 0 && (
        <ul className="mt-1 list-disc space-y-0.5 ps-5 text-ink-2">
          {excluded.map(([k, n]) => (
            <li key={k}>{t(`admin.messaging.skipReasons.${k}`, { count: n })}</li>
          ))}
        </ul>
      )}
      {channel === 'sms' && estimate.partsPerMessage > 0 && (
        <p className="mt-1 text-ink-2">
          {t('admin.messaging.partsPerMessage', { count: estimate.partsPerMessage })}
        </p>
      )}
      <p className="mt-1 text-ink-2">
        {estimate.costMinor !== null
          ? t('admin.messaging.costEstimate', { cost: money(estimate.costMinor, TRY) })
          : t('admin.messaging.costUnknown')}
      </p>
      <p className="mt-1 text-ink-3">{t('admin.messaging.estimateNote')}</p>
    </div>
  );
}

function SendButton({
  campaign,
  dirty,
  channelLive,
}: {
  campaign: MessageCampaign;
  dirty: boolean;
  channelLive: boolean;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [later, setLater] = useState(false);
  const [at, setAt] = useState(() => toLocalInput(new Date(Date.now() + 3_600_000)));
  const [estimate, setEstimate] = useState<MessageAudienceEstimate | null>(null);
  const idem = useRef(new IdempotencyKey());
  const est = useApiMutation(
    () =>
      api.post<MessageAudienceEstimate>('/v1/admin/message-campaigns/audience', {
        channel: campaign.channel,
        segmentIds: campaign.segmentIds,
        smsTemplateId: campaign.smsTemplateId,
      }),
    { onSuccess: (r) => setEstimate(r) },
  );
  const send = useApiMutation(
    () => {
      const b = { scheduledAt: later && at ? new Date(at).toISOString() : null, version: campaign.version };
      return api.post<MessageCampaign>(`/v1/admin/message-campaigns/${campaign.id}/schedule`, b, {
        idempotencyKey: idem.current.for(JSON.stringify(b)),
      });
    },
    {
      invalidate: [['message-campaigns'], ['message-campaign', campaign.id]],
      success: later ? t('admin.messaging.scheduledToast') : t('admin.messaging.queuedToast'),
      silentFieldErrors: true,
      onSuccess: () => setOpen(false),
    },
  );
  const smsBlocked = campaign.channel === 'sms' && !!campaign.smsTemplate && !campaign.smsTemplate.usable;
  const conflict = send.error instanceof ApiError && !send.error.errors.length ? send.error.title : null;
  return (
    <>
      <span className="inline-flex items-center gap-1">
        <Button
          variant="primary"
          disabled={dirty || smsBlocked}
          onClick={() => {
            idem.current = new IdempotencyKey();
            send.reset();
            setEstimate(null);
            setOpen(true);
            est.mutate(undefined);
          }}
        >
          <Send className="h-4 w-4" aria-hidden />
          {t('admin.messaging.send')}
        </Button>
        <HelpTip k="messaging.send" />
      </span>
      {smsBlocked && <span className="text-sm text-ink-2">{t('admin.messaging.templateNotApproved')}</span>}
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t('admin.messaging.sendTitle', { channel: t(`admin.messaging.channels.${campaign.channel}`) })}
        description={t('admin.messaging.sendLead')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={send.isPending || !estimate?.eligible || (later && !at)}
              onClick={() => send.mutate(undefined)}
            >
              {later
                ? t('admin.messaging.scheduleConfirm')
                : t('admin.messaging.sendConfirm', { count: estimate?.eligible ?? 0 })}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {!channelLive && (
            <p className="rounded-md border border-amber/30 bg-amber-tint px-3 py-2 text-sm">
              {t('admin.messaging.sendSetupWarning')}
            </p>
          )}
          {!estimate ? (
            <Spinner label={t('common.loading')} />
          ) : (
            <EstimateView estimate={estimate} channel={campaign.channel} />
          )}
          <fieldset className="space-y-1.5 text-sm">
            <legend className="mb-1 font-medium">{t('admin.campaigns.when')}</legend>
            <label className="flex items-center gap-2">
              <input type="radio" name="when" checked={!later} onChange={() => setLater(false)} />
              {t('admin.campaigns.now')}
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" name="when" checked={later} onChange={() => setLater(true)} />
              {t('admin.campaigns.later')}
            </label>
          </fieldset>
          {later && (
            <Field
              label={t('admin.campaigns.scheduledAt')}
              help={<HelpTip k="messaging.scheduled_at" />}
              error={fieldError(send.error, 'scheduledAt')}
            >
              {(p) => (
                <Input {...p} type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
              )}
            </Field>
          )}
          {[
            fieldError(send.error, 'segmentIds'),
            fieldError(send.error, 'whatsapp'),
            fieldError(send.error, 'smsTemplateId'),
            conflict,
          ]
            .filter(Boolean)
            .map((m) => (
              <p key={m} role="alert" className="text-sm font-medium text-rose">
                {m}
              </p>
            ))}
        </div>
      </Dialog>
    </>
  );
}

function StatusSection({
  campaign,
  canSend,
  onCancel,
  onResume,
  busy,
}: {
  campaign: MessageCampaign;
  canSend: boolean;
  onCancel: () => Promise<unknown>;
  onResume: () => void;
  busy: boolean;
}) {
  const t = useT();
  const { formatDate } = useI18n();
  const p = campaign.progress;
  const done = p ? p.total - p.queued : 0;
  const reason = campaign.pauseReason;
  return (
    <Section title={t('admin.messaging.statusTitle')} action={<HelpTip k="messaging.status" />}>
      <div className="space-y-3 text-sm">
        <KeyValue
          items={[
            {
              label: t('admin.messaging.statusLabel'),
              value: t(`admin.messaging.statuses.${campaign.status}`),
            },
            ...(campaign.scheduledAt
              ? [{ label: t('admin.campaigns.scheduledAt'), value: formatDate(campaign.scheduledAt) }]
              : []),
            ...(campaign.startedAt
              ? [{ label: t('admin.campaigns.startedAt'), value: formatDate(campaign.startedAt) }]
              : []),
            ...(campaign.finishedAt
              ? [{ label: t('admin.campaigns.finishedAt'), value: formatDate(campaign.finishedAt) }]
              : []),
          ]}
        />
        {p && p.total > 0 && (
          <div className="max-w-xl space-y-1">
            <progress
              className="h-2 w-full accent-[var(--zt-pine)]"
              max={p.total}
              value={done}
              aria-label={t('admin.messaging.progressLabel')}
            />
            <p className="text-ink-2" aria-live="polite">
              {t('admin.messaging.progress', {
                done,
                total: p.total,
                sent: p.sent,
                delivered: p.delivered,
                skipped: p.skipped,
                failed: p.failed,
              })}
            </p>
          </div>
        )}
        {campaign.status === 'paused' && reason && (
          <p role="alert" className="rounded-md border border-rose/30 bg-rose-tint px-3 py-2 text-rose">
            {PAUSE_KEYS.includes(reason)
              ? t(`admin.messaging.pauseReasons.${reason}`)
              : t('admin.messaging.pauseReasonOther', { code: reason })}
          </p>
        )}
        {canSend && (
          <div className="flex flex-wrap items-center gap-2">
            {campaign.status === 'paused' && (
              <>
                <Button size="sm" variant="primary" onClick={onResume} disabled={busy}>
                  <Play className="h-4 w-4" aria-hidden />
                  {t('admin.messaging.resume')}
                </Button>
                <HelpTip k="messaging.resume" />
              </>
            )}
            {['scheduled', 'sending', 'paused'].includes(campaign.status) && (
              <>
                <ConfirmButton
                  variant="secondary"
                  message={t('admin.messaging.cancelConfirm')}
                  onConfirm={onCancel}
                  disabled={busy}
                >
                  <Pause className="h-4 w-4" aria-hidden />
                  {t('admin.messaging.cancel')}
                </ConfirmButton>
                <HelpTip k="messaging.cancel" />
              </>
            )}
          </div>
        )}
      </div>
    </Section>
  );
}

function ReportSection({ campaign }: { campaign: MessageCampaign }) {
  const t = useT();
  const money = useMoney();
  const live = LIVE.includes(campaign.status);
  const q = useQuery({
    queryKey: ['message-campaign-report', campaign.id],
    queryFn: () => api.get<MessageCampaignReport>(`/v1/admin/message-campaigns/${campaign.id}/report`),
    refetchInterval: live ? 5000 : false,
  });
  const r = q.data;
  if (!r) return <Spinner label={t('common.loading')} />;
  const tt = r.totals;
  const stats: Array<[string, string]> = [
    ['recipients', String(tt.recipients)],
    ['sent', String(tt.sent)],
    ['delivered', String(tt.delivered)],
    ['failed', String(tt.failed)],
    ['skipped', String(tt.skipped)],
    ...(campaign.channel === 'sms' ? ([['parts', String(tt.parts)]] as Array<[string, string]>) : []),
    ['cost', r.costMinor !== null ? money(r.costMinor, TRY) : '—'],
  ];
  const skips = SKIP_KEYS.filter((k) => (r.skipReasons[k] ?? 0) > 0);
  const errors = Object.entries(r.errors).filter(([, n]) => n > 0);
  return (
    <Section
      title={t('admin.messaging.reportTitle')}
      description={t('admin.messaging.reportLead')}
      action={<HelpTip k="messaging.report" />}
    >
      <div className="space-y-6 text-sm">
        {r.purged && <p className="text-ink-2">{t('admin.messaging.purged')}</p>}
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {stats.map(([k, v]) => (
            <div key={k} className="rounded-md border border-line bg-surface px-3 py-2">
              <dt className="text-ink-3">{t(`admin.messaging.stats.${k}`)}</dt>
              <dd className="font-cond text-xl font-semibold tabular-nums">{v}</dd>
            </div>
          ))}
        </dl>
        {skips.length > 0 && (
          <div>
            <p className="mb-1 font-medium">{t('admin.messaging.skippedTitle')}</p>
            <ul className="list-disc space-y-0.5 ps-5 text-ink-2">
              {skips.map((k) => (
                <li key={k}>{t(`admin.messaging.skipReasons.${k}`, { count: r.skipReasons[k] ?? 0 })}</li>
              ))}
            </ul>
          </div>
        )}
        {errors.length > 0 && (
          <div>
            <p className="mb-1 font-medium">{t('admin.messaging.errorsTitle')}</p>
            <ul className="list-disc space-y-0.5 ps-5 text-ink-2">
              {errors.map(([k, n]) => (
                <li key={k}>
                  {KNOWN_ERRORS.includes(k)
                    ? t(`admin.messaging.sendErrors.${k}`, { count: n })
                    : t('admin.messaging.providerError', { code: k, count: n })}
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-ink-3">{t('admin.messaging.reportNote')}</p>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------- SMS şablonları

export function SmsTemplatesPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['sms-templates'],
    queryFn: () => api.get<SmsTemplate[]>('/v1/admin/sms-templates'),
  });
  return (
    <PageFrame
      helpKey="page.sms_templates"
      actions={
        can('marketing.campaign.write') && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              onClick={() => void navigate({ to: '/sms-sablonlari/$id', params: { id: 'yeni' } })}
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.smsTemplates.create')}
            </Button>
            <HelpTip k="sms_templates.create" />
          </div>
        )
      }
    >
      <div className="space-y-6">
        <p className="max-w-[70ch] text-sm text-ink-2">{t('admin.smsTemplates.lead')}</p>
        {!list.data ? (
          <Spinner label={t('common.loading')} />
        ) : !list.data.length ? (
          <EmptyState
            title={t('admin.smsTemplates.emptyTitle')}
            description={t('admin.smsTemplates.emptyLead')}
          />
        ) : (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full text-sm" aria-label={t('admin.smsTemplates.listLabel')}>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.smsTemplates.colName')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.smsTemplates.colReview')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.smsTemplates.colUpdated')}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((r) => (
                  <tr key={r.id} className="border-t border-line align-top">
                    <td className="max-w-0 px-3 py-2">
                      <Link
                        to="/sms-sablonlari/$id"
                        params={{ id: r.id }}
                        className="font-medium text-pine hover:underline"
                      >
                        {r.name}
                      </Link>
                      <p className="truncate text-ink-3">{r.body}</p>
                    </td>
                    <td className="px-3 py-2">
                      {r.review.required || r.review.status !== 'not_required' ? (
                        <Badge tone={reviewTone[r.review.status]}>
                          {t(`admin.pages.reviews.${r.review.status}`)}
                        </Badge>
                      ) : (
                        <span className="text-ink-3">{t('admin.smsTemplates.noReview')}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-end text-ink-2">{formatDate(r.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PageFrame>
  );
}

export function SmsTemplateEditorPage() {
  const { id } = useParams({ from: '/app/sms-sablonlari/$id' });
  const isNew = id === 'yeni';
  const t = useT();
  const q = useQuery({
    queryKey: ['sms-template', id],
    queryFn: () => api.get<SmsTemplate>(`/v1/admin/sms-templates/${id}`),
    enabled: !isNew,
  });
  if (!isNew && !q.data) return <Spinner label={t('common.loading')} />;
  return <SmsTemplateEditor key={q.data?.id ?? 'yeni'} template={q.data ?? null} />;
}

function SmsTemplateEditor({ template }: { template: SmsTemplate | null }) {
  const t = useT();
  const { formatDate } = useI18n();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can('marketing.campaign.write');
  const [name, setName] = useState(template?.name ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  useEffect(() => {
    if (template) {
      setName(template.name);
      setBody(template.body);
    }
  }, [template]);
  const area = useRef<HTMLTextAreaElement | null>(null);
  const dirty = !template || name !== template.name || body !== template.body;
  const complete = name.trim().length >= 2 && !!body.trim();
  const debounced = useDebounced(body, 400);
  const preview = useQuery({
    queryKey: ['sms-preview', debounced],
    queryFn: () => api.post<SmsPreview>('/v1/admin/sms-templates/preview', { body: debounced }),
    enabled: !!debounced.trim(),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const save = useApiMutation(
    () =>
      template
        ? api.patch<SmsTemplate>(`/v1/admin/sms-templates/${template.id}`, {
            name,
            body,
            version: template.version,
          })
        : api.post<SmsTemplate>('/v1/admin/sms-templates', { name, body }),
    {
      invalidate: [['sms-templates'], ...(template ? [['sms-template', template.id]] : [])],
      success: t('admin.smsTemplates.saved'),
      onSuccess: (r) => {
        if (!template) void navigate({ to: '/sms-sablonlari/$id', params: { id: r.id } });
      },
    },
  );
  const review = useApiMutation(
    (v: { decision: 'approved' | 'rejected'; note: string }) =>
      api.post<SmsTemplate>(`/v1/admin/sms-templates/${template!.id}/review`, {
        decision: v.decision,
        note: v.note || null,
        contentHash: template!.review.contentHash,
        version: template!.version,
      }),
    {
      invalidate: [['sms-templates'], ['sms-template', template?.id]],
      success: t('admin.pages.reviewedToast'),
    },
  );
  const remove = useApiMutation(() => api.del(`/v1/admin/sms-templates/${template!.id}`), {
    invalidate: [['sms-templates']],
    success: t('admin.smsTemplates.deleted'),
    onSuccess: () => void navigate({ to: '/sms-sablonlari' }),
  });
  const insert = (field: (typeof MERGE)[number]) => {
    const token = `{{${field}}}`;
    const el = area.current;
    const start = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? body.length;
    const next = `${body.slice(0, start)}${token}${body.slice(end)}`.slice(0, 900);
    setBody(next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };
  const fe = (f: string) => fieldError(save.error, f);
  const p = preview.data;

  return (
    <PageFrame
      helpKey="page.sms_template_editor"
      title={template ? template.name : t('admin.smsTemplates.create')}
      actions={
        canWrite && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              onClick={() => save.mutate(undefined)}
              disabled={!dirty || !complete || save.isPending}
            >
              {template ? t('admin.smsTemplates.save') : t('admin.smsTemplates.createConfirm')}
            </Button>
            <HelpTip k="sms_templates.save" />
          </div>
        )
      }
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Link to="/sms-sablonlari" className="text-pine hover:underline">
          {t('admin.smsTemplates.back')}
        </Link>
        {template && (template.review.required || template.review.status !== 'not_required') && (
          <Badge tone={reviewTone[template.review.status]}>
            {t(`admin.pages.reviews.${template.review.status}`)}
          </Badge>
        )}
        {template && dirty && <Badge tone="amber">{t('admin.smsTemplates.unsaved')}</Badge>}
      </div>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <fieldset disabled={!canWrite} className="grid content-start gap-4">
          <legend className="sr-only">{t('admin.smsTemplates.editor')}</legend>
          <Field
            label={t('admin.smsTemplates.name')}
            description={t('admin.smsTemplates.nameLead')}
            help={<HelpTip k="sms_templates.name" />}
            error={fe('name')}
            required
          >
            {(f) => <Input {...f} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field
            label={t('admin.smsTemplates.body')}
            description={t('admin.smsTemplates.bodyLead')}
            help={<HelpTip k="sms_templates.body" />}
            error={fe('body')}
            required
          >
            {(f) => (
              <Textarea
                {...f}
                ref={area}
                rows={6}
                value={body}
                maxLength={900}
                onChange={(e) => setBody(e.target.value)}
              />
            )}
          </Field>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-ink-2">{t('admin.smsTemplates.insertField')}</span>
            {MERGE.map((m) => (
              <Button key={m} size="sm" onClick={() => insert(m)}>
                {t(`admin.smsTemplates.fields.${m}`)}
              </Button>
            ))}
            <HelpTip k="sms_templates.fields" />
          </div>
        </fieldset>

        <section aria-labelledby="sms-preview-title" className="space-y-3">
          <h2 id="sms-preview-title" className="flex items-center gap-1 font-medium">
            {t('admin.smsTemplates.preview')}
            <HelpTip k="sms_templates.preview" />
          </h2>
          {!body.trim() ? (
            <p className="text-sm text-ink-3">{t('admin.smsTemplates.previewEmpty')}</p>
          ) : !p ? (
            <Spinner label={t('common.loading')} />
          ) : (
            <div className="space-y-3 text-sm" aria-live="polite">
              <p className="whitespace-pre-wrap rounded-2xl rounded-bl-sm bg-surface-2 px-4 py-3 text-ink">
                {p.text}
              </p>
              <KeyValue
                items={[
                  {
                    label: t('admin.smsTemplates.encoding'),
                    value: t(`admin.smsTemplates.encodings.${p.encoding}`),
                  },
                  { label: t('admin.smsTemplates.length'), value: String(p.length) },
                  {
                    label: t('admin.smsTemplates.parts'),
                    value: t('admin.messaging.partsPerMessage', { count: p.parts }),
                  },
                ]}
              />
              {p.parts > 1 && (
                <p className="text-ink-2">{t('admin.smsTemplates.multiPart', { count: p.parts })}</p>
              )}
              {p.encoding === 'unicode' && (
                <p className="rounded-md border border-amber/30 bg-amber-tint px-3 py-2">
                  {t('admin.smsTemplates.unicodeWarning')}
                </p>
              )}
              {p.parts > 7 && (
                <p role="alert" className="rounded-md border border-rose/30 bg-rose-tint px-3 py-2 text-rose">
                  {t('admin.smsTemplates.tooLong')}
                </p>
              )}
              {p.footerMissing.length > 0 && (
                <div className="rounded-md border border-amber/30 bg-amber-tint px-3 py-2">
                  <p>{t('admin.smsTemplates.footerMissing')}</p>
                  <ul className="mt-1 list-disc space-y-1 ps-5">
                    {p.footerMissing.map((m) => (
                      <li key={m}>
                        <Link
                          to={m === 'mersis' ? '/isletme' : ROUTE}
                          className="inline-flex min-h-6 items-center text-pine hover:underline"
                        >
                          {t(`admin.smsTemplates.footerParts.${m}`)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {p.findings.length > 0 && (
                <p className="rounded-md border border-amber/30 bg-amber-tint px-3 py-2">
                  {t('admin.smsTemplates.findingsNote', { count: p.findings.length })}
                </p>
              )}
              <p className="text-ink-3">{t('admin.smsTemplates.previewNote')}</p>
            </div>
          )}
        </section>
      </div>

      {template && (
        <ContentReviewPanel
          status={template.review.status}
          note={template.review.note}
          findings={template.review.findings}
          required={template.review.required}
          dirty={dirty}
          canReview={can('content.expert_review')}
          busy={review.isPending}
          onDecide={(decision, note) => review.mutate({ decision, note })}
        />
      )}

      {template && canWrite && (
        <Section title={t('admin.smsTemplates.dangerZone')}>
          <div className="flex items-center gap-2">
            <ConfirmButton
              message={t('admin.smsTemplates.deleteConfirm', { name: template.name })}
              onConfirm={() => remove.mutateAsync(undefined).catch(() => undefined)}
            >
              {t('common.delete')}
            </ConfirmButton>
            <HelpTip k="sms_templates.delete" />
          </div>
        </Section>
      )}
      {template && (
        <p className="text-sm text-ink-3">
          {t('admin.smsTemplates.updated', { date: formatDate(template.updatedAt) })}
        </p>
      )}
    </PageFrame>
  );
}
